import { useState, type FormEvent, type ReactElement } from 'react';
import { X } from 'lucide-react';
import { WORKSPACE_ROLES, type WorkspaceMember, type WorkspaceRole } from '@molt/shared';
import {
  Avatar,
  Button,
  IconButton,
  Menu,
  Notice,
  ProfileLink,
  profileName,
  TextField,
} from '@/components';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * `FE-07.2` — the workspace member list, roles rendered and role changes
 * surfaced. Chunk `D2`, agent D. `FR-PERM-014`, `FR-PERM-021`.
 *
 * Purely presentational: the route owns the queries, the mutations and every
 * `announce()`.
 *
 * ---------------------------------------------------------------------------
 * THREE OUTCOMES, AND FOLDING TWO OF THEM LOSES A REQUIREMENT
 * ---------------------------------------------------------------------------
 * `viewerRole` is the whole of `FR-PERM-014` AC3 and `FR-PERM-021` AC3:
 *
 *  - `'admin'`  → every role, a role control per row, and the add-member form;
 *  - `'normal'` → every role **as text**, no role control, **no add-member
 *    form**. Not a disabled control with a stated reason: `entry-surfaces.md`
 *    D6's locked-with-reason pattern is for a capability whose reason the
 *    surface can state, and D6 itself records that Phase 1 cannot populate one
 *    here;
 *  - `null`     → read-only, because the caller's role is not yet known. A
 *    control that appears a moment late beats one that appears and 403s.
 *
 * ⚠ **The role control is never rendered on the viewer's own row.** A
 * self-demotion is the one demotion the last-admin 409 exists to stop, and a
 * control that reliably fails is worse than no control.
 *
 * ⚠ The role `Menu`'s section relies on `onValueChange`, not the per-item
 * `onSelect` — `Menu.test.tsx`'s own "selection" suite wires the working
 * example that way (each item's `onSelect` is a no-op) and this file follows
 * it rather than inventing a second path for the same click.
 *
 * `memberErrors` is keyed by user id so a remove or role refusal renders on
 * the row whose control produced it. `roleError` stays supported for the
 * shipped route composition, which still has only a page-level role message.
 */
export interface WorkspaceMemberListProps {
  members: readonly WorkspaceMember[];
  /** The caller's own role. `null` renders the list read-only — §5.3. */
  viewerRole: WorkspaceRole | null;
  /**
   * SA-9 — the admin capability, when the caller has one without an admin row
   * (a platform superadmin, D10). Omitted, it is `viewerRole === 'admin'`.
   */
  viewerCanAdminister?: boolean | undefined;
  /** The caller's own user id, so their row can be marked and the role control suppressed on it. */
  viewerId: string | undefined;
  onChangeRole: (userId: string, role: WorkspaceRole) => void;
  onAddMember: (handle: { email: string } | { username: string }) => void;
  /** Optional until the route owns the DELETE mutation; when present, admins get remove controls. */
  onRemoveMember?: (userId: string) => void;
  /** Per-user-id busy flag for row controls. */
  busyMemberId?: string | null | undefined;
  /** Per-user-id busy flag for the role control. Kept for shipped callers. */
  busyRoleFor?: string | null | undefined;
  addBusy?: boolean;
  /** Server messages, verbatim. `addNotice` carries the already-a-member case — §5.7. */
  roleError?: string;
  addError?: string;
  addNotice?: string;
  /** Row-level server messages, keyed by `WorkspaceMember.id`. */
  memberErrors?: Readonly<Record<string, string>>;
  className?: string;
}

/**
 * FE-80.1 — CATALOGUE KEYS, not English, and the annotation is load-bearing.
 *
 * ⚠ A module-level constant cannot call `t()` (it has no context), so the table
 * holds the key and every read resolves it at render. `BoardMapView.tsx:80–84`
 * is the precedent; holding the English of the moment leaves the previous
 * language on screen after the catalogue query resolves, and nothing in this
 * repository can see that.
 *
 * ⚠ `Record<WorkspaceRole, string>` stays EXPLICIT. It is the only thing making
 * a missing or misspelled arm a typecheck error: `t()` returns the KEY on a
 * miss rather than throwing, so a dropped arm would render
 * `workspaceMembers.role.normal` into the page with nothing red.
 *
 * ⚠ The RECORD'S KEYS (`admin`, `normal`) are the wire values `WORKSPACE_ROLES`
 * declares and `workspaceRoleSchema` parses — they stay literal. Only the
 * VALUES are copy.
 */
const ROLE_LABEL_KEYS: Record<WorkspaceRole, string> = {
  admin: 'workspaceMembers.role.admin',
  normal: 'workspaceMembers.role.normal',
};

export function WorkspaceMemberList({
  members,
  viewerRole,
  viewerCanAdminister,
  viewerId,
  onChangeRole,
  onAddMember,
  onRemoveMember,
  busyMemberId,
  busyRoleFor,
  addBusy = false,
  roleError,
  addError,
  addNotice,
  memberErrors = {},
  className,
}: WorkspaceMemberListProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const canManage = viewerCanAdminister ?? viewerRole === 'admin';
  const busyRowId = busyMemberId ?? busyRoleFor ?? null;

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {roleError !== undefined ? <Notice tone="bad" body={roleError} /> : null}

      {/*
        Explicit roles, not left to the implicit ones: Tailwind's preflight
        sets `list-style: none` on every `ul`, which strips the implicit
        `list`/`listitem` roles in WebKit/VoiceOver (jsdom applies no CSS and
        cannot catch this — §7's fixed roles for "Member list" and "a member
        row"). `jsx-a11y/no-redundant-roles` is not configured in this repo's
        `eslint.config.js`, so the explicit role is not a lint conflict.
      */}
      <ul role="list" className="flex flex-col gap-2">
        {members.map((member) => {
          const isViewer = viewerId !== undefined && member.id === viewerId;
          const showControl = canManage && !isViewer;
          const showRemove = canManage && !isViewer && onRemoveMember !== undefined;
          const busy = busyRowId === member.id;
          const rowError = memberErrors[member.id];

          return (
            <li
              key={member.id}
              role="listitem"
              className="flex flex-col gap-1 rounded-card border border-border-strong bg-surface-raised p-3"
            >
              <div className="flex items-center gap-3">
                <Avatar profile={member} size="sm" decorative />
                <span className="min-w-0 flex-1 truncate text-ink">
                  <ProfileLink profile={member} />
                  {isViewer ? (
                    // A sibling, not appended into the same text node: the
                    // absent role control on this one row would otherwise read
                    // as a rendering bug rather than "this is you"
                    // (`viewerId`'s own docstring — "so their row can be
                    // marked"). Kept out of the name span so `getByText` on
                    // the bare display name still matches exactly one node.
                    <span className="ml-1 text-ink-muted">
                      {t('workspaceMembers.you')}
                    </span>
                  ) : null}
                </span>
                {member.deactivated === true ? (
                  /*
                    ⚠ NOT A REDACTION. `FR-PERM-034` AC3 keeps a deactivated
                    member's authored content "visible and attributed", so this
                    row keeps its name, its avatar and its role and GAINS a
                    state word beside them. Deactivation is a distinct action
                    from removal, and the row says so.
                    `enterprises.$enterpriseId.policy.tsx` renders the same fact
                    the same way for the Enterprise-admin audience; this is that
                    shipped precedent, not a second reading of it.

                    ⚠ OUTSIDE THE NAME SPAN, AND THAT PLACEMENT IS A FIXED
                    DEFECT RATHER THAN A PREFERENCE. It first shipped INSIDE
                    `<span className="min-w-0 flex-1 truncate …">`, where
                    `truncate` is `overflow:hidden; text-overflow:ellipsis;
                    white-space:nowrap` — so a member whose display name filled
                    the span had this word laid out past the clip and painted
                    NOWHERE. Five green unit cases could not see it (jsdom
                    applies no CSS, and `getByText` matches a clipped node
                    exactly as well as a visible one); the browser capture
                    `fe11-workspace-member-projection` frame 3 measured it —
                    the word's box began at x=686.7 inside a span ending at
                    x=536.3, 150px past the edge — and the frame showed a
                    deactivated row indistinguishable from an active one.
                    `shrink-0` keeps it from being the thing the flex row
                    squeezes instead.

                    ⚠ Still a SIBLING node, never appended into the name's text
                    node, so `getByText` on the bare display name matches
                    exactly one node. The `(you)` marker inside the span above
                    is the same shape and carries the same latent clip; it is
                    left alone here because it marks the VIEWER's own row, whose
                    identity they already know, and because that element belongs
                    to an earlier chunk's oracles.

                    ⚠ `=== true`, NEVER `!member.deactivated`. The field is
                    `.optional()` and the projection emits it only when true —
                    `toWorkspaceMember` in `routes/workspaces.ts` states the
                    consumer rule in as many words — so the key is absent for
                    every active member and truthiness would read an absent key
                    and an explicit `false` as the same thing.
                  */
                  <span className="shrink-0 text-sm text-bad">
                    {t('workspaceMembers.deactivated')}
                  </span>
                ) : null}
                {showControl ? (
                  <RoleControl member={member} busy={busy} onChangeRole={onChangeRole} />
                ) : (
                  <span className="shrink-0 text-sm text-ink-muted">
                    {t(ROLE_LABEL_KEYS[member.role])}
                  </span>
                )}

                {showRemove ? (
                  <IconButton
                    label={t('workspaceMembers.remove', {
                      name: profileName(member),
                    })}
                    size="sm"
                    busy={busy}
                    disabled={busy}
                    onClick={() => {
                      onRemoveMember(member.id);
                    }}
                  >
                    <X aria-hidden="true" className="size-3.5" />
                  </IconButton>
                ) : null}
              </div>

              {rowError !== undefined ? (
                <span className="text-xs text-bad">{rowError}</span>
              ) : null}
            </li>
          );
        })}
      </ul>

      {canManage ? (
        <AddMemberForm
          onAddMember={onAddMember}
          busy={addBusy}
          error={addError}
          notice={addNotice}
        />
      ) : null}
    </div>
  );
}

function RoleControl({
  member,
  busy,
  onChangeRole,
}: {
  member: WorkspaceMember;
  busy: boolean;
  onChangeRole: (userId: string, role: WorkspaceRole) => void;
}) {
  const { t } = useInterfaceLanguage();
  const name = profileName(member);
  /*
   * ⚠ Resolved ONCE and fed to BOTH nodes below — the trigger's `aria-label`
   * and its visible text. WCAG 2.5.3 *Label in Name*: the accessible name must
   * CONTAIN the visible label. Resolving `ROLE_LABEL_KEYS[member.role]` twice
   * reads identically in English and can drift in any other language, with
   * nothing red. `D2-BRIEF.md` §7 rule 1.
   */
  const roleLabel = t(ROLE_LABEL_KEYS[member.role]);

  return (
    <Menu
      align="end"
      trigger={
        // ⚠ Never `disabled` on the trigger. §5.5: "the role control is
        // always enabled for an admin" — and `Button`'s own docstring names
        // the mechanism reason: a focused control that goes `disabled`
        // drops focus to `<body>` mid-interaction, which is exactly the
        // moment a click just landed here. `aria-busy` carries the signal
        // instead, and the two radio items carry the refusal — `Menu.tsx`'s
        // docstring: `MenuItem.disabled` is for "an action that is
        // momentarily unavailable," which is this case exactly.
        <button
          type="button"
          aria-busy={busy}
          aria-label={t('workspaceMembers.roleControl', {
            name,
            role: roleLabel,
          })}
          className="inline-flex h-7 shrink-0 items-center gap-1 rounded-card border border-border-strong bg-surface-raised px-2 text-sm text-ink"
        >
          {roleLabel}
        </button>
      }
      sections={[
        {
          id: 'role',
          selection: 'radio',
          value: member.role,
          onValueChange: (value) => {
            onChangeRole(member.id, value as WorkspaceRole);
          },
          /*
           * ⚠ `id` is the WIRE and `label` is the COPY, two lines apart in one
           * object literal. `id` is what `onValueChange` hands straight to
           * `onChangeRole` and thence to `PATCH …/members/:userId/role`, which
           * `workspaceRoleSchema.parse` answers 422 to for anything outside
           * `WORKSPACE_ROLES`. Routing `id` — or the section's `value` above —
           * sends a translated string to the server, and the failure surfaces
           * as a 4xx that reads like an unrelated bug.
           */
          items: WORKSPACE_ROLES.map((role) => ({
            id: role,
            label: t(ROLE_LABEL_KEYS[role]),
            disabled: busy,
            // The actual change is wired through the section's own
            // `onValueChange` above — `Menu.test.tsx`'s "selection" suite is
            // the shipped precedent for a no-op per-item `onSelect` here.
            // `() => undefined`, not `() => {}`: an empty arrow body trips
            // `@typescript-eslint/no-empty-function` outside test files.
            onSelect: () => undefined,
          })),
        },
      ]}
    />
  );
}

function AddMemberForm({
  onAddMember,
  busy,
  error,
  notice,
}: {
  onAddMember: (handle: { email: string } | { username: string }) => void;
  busy: boolean;
  error: string | undefined;
  notice: string | undefined;
}) {
  const { t } = useInterfaceLanguage();
  const [value, setValue] = useState('');

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = value.trim();
    if (trimmed === '') return;
    onAddMember(trimmed.includes('@') ? { email: trimmed } : { username: trimmed });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      {notice !== undefined ? <Notice tone="info" body={notice} /> : null}
      {error !== undefined ? <Notice tone="bad" body={error} /> : null}
      <div className="flex items-end gap-2">
        <TextField
          label={t('workspaceMembers.addLabel')}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
          wrapperClassName="flex-1"
        />
        <Button type="submit" variant="primary" busy={busy}>
          {t('workspaceMembers.addAction')}
        </Button>
      </div>
    </form>
  );
}
